varying vec3 vNormal;
varying vec3 vNormalWorld; // Normal in world space for IBL
varying vec3 vPosition;
varying vec2 vUv;

uniform vec3 color;
uniform vec3 lightDirection;
uniform vec3 ambientLightColor;
uniform vec3 directionalLightColor;
uniform float ambientLightIntensity;
uniform float directionalLightIntensity;
uniform bool showNormals;
uniform bool useEnvironmentLighting;
uniform float environmentLightingIntensity;
uniform vec3 shLightProbe[9]; // Spherical harmonics coefficients

// Function to evaluate spherical harmonics for light probe irradiance
vec3 getLightProbeIrradiance(vec3 normal) {
    // Spherical harmonics evaluation for L2 (9 coefficients)
    float x = normal.x, y = normal.y, z = normal.z;
    vec3 result = shLightProbe[0] * 0.282095; // L00
    result += shLightProbe[1] * 0.488603 * y; // L1-1
    result += shLightProbe[2] * 0.488603 * z; // L10
    result += shLightProbe[3] * 0.488603 * x; // L11
    result += shLightProbe[4] * 1.092548 * x * y; // L2-2
    result += shLightProbe[5] * 1.092548 * y * z; // L2-1
    result += shLightProbe[6] * 0.315392 * (3.0 * z * z - 1.0); // L20
    result += shLightProbe[7] * 1.092548 * x * z; // L21
    result += shLightProbe[8] * 0.546274 * (x * x - y * y); // L22
    return result;
}

void main() {
    if (showNormals) {
        // Normal visualization: convert normal from [-1, 1] to [0, 1] range
        vec3 normalColor = vNormal * 0.5 + 0.5;
        gl_FragColor = vec4(normalColor, 1.0);
    } else {
        vec3 finalColor;
        
        if (useEnvironmentLighting) {
            // Use light probe for environment-based lighting
            // Use world space normal for correct IBL evaluation
            vec3 irradiance = getLightProbeIrradiance(normalize(vNormalWorld));
            
            // Ensure minimum lighting to avoid black material
            // If irradiance is too dark, add fallback ambient lighting
            vec3 minIrradiance = vec3(0.03); // Minimum ambient lighting (reduced to preserve detail)
            irradiance = max(irradiance, minIrradiance);
            
            // Base color from IBL
            vec3 iblColor = color * irradiance * environmentLightingIntensity;
            
            // Add strong directional light to emphasize surface relief and details
            // This is critical when IBL is from a flat/dark background (basic env)
            vec3 light = normalize(lightDirection);
            float dotNL = max(dot(vNormal, light), 0.0);
            
            // Use stronger directional light with better contrast
            // Apply power curve to enhance contrast between lit and unlit areas
            float lightIntensity = pow(dotNL, 0.8); // Slightly softer falloff for better detail
            vec3 directionalLight = color * lightIntensity * 1.2; // Stronger directional light
            vec3 ambient = vec3(0.15); // Ambient for unlit areas
            
            // Mix IBL with directional light - use more directional light for better shape visibility
            // 40% IBL + 60% directional light to emphasize form
            vec3 directionalMix = ambient + directionalLight;
            finalColor = iblColor * 0.4 + directionalMix * 0.6;
            
            // Add some rim lighting for better visualization
            // Use view direction from camera (passed via uniform or computed from viewMatrix)
            vec3 viewDir = normalize(-vPosition); // Simplified: use negative position as view direction
            float rim = pow(1.0 - max(dot(viewDir, normalize(vNormal)), 0.0), 2.0);
            vec3 rimLight = vec3(0.25) * rim;
            finalColor += rimLight;
        } else {
            // Use directional lighting (original implementation)
            vec3 light = normalize(lightDirection);

            // Calculate lighting
            float dotNL = max(dot(vNormal, light), 0.0);
            vec3 ambient = ambientLightColor * ambientLightIntensity;
            vec3 diffuse = color * directionalLightColor * dotNL * directionalLightIntensity;

            // Add some rim lighting for better visualization
            // Use view direction from camera (simplified)
            vec3 viewDir = normalize(-vPosition); // Simplified: use negative position as view direction
            float rim = pow(1.0 - max(dot(viewDir, vNormal), 0.0), 2.0);
            vec3 rimLight = vec3(0.3) * rim;

            finalColor = ambient + diffuse + rimLight;
        }
        
        gl_FragColor = vec4(finalColor, 1.0);
    }
}